import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Divider,
  Radio,
  FormControlLabel,
  Chip,
  alpha,
  InputAdornment,
} from "@mui/material";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";
import {
  PaymentOutlined,
  LocationOn,
  PersonOutline,
  PhoneOutlined,
  ApartmentOutlined,
  LocationCityOutlined,
  ReceiptLongOutlined,
  CheckCircleOutline,
  ArrowForwardIos,
  HomeOutlined,
} from "@mui/icons-material";
import configLocal from "../../config/config.local.json";
const COLORS = {
  primary: "#2E4053",
  accent: "#2E4053",
  accentHover: "#1C2A3D",
  accentLight: "#D1D8E0",
  surface: "#D1D8E0",
  border: "#A6AAAD",
  text: "#2E4053",
  textMuted: "#34495E",
  textMutedLight: "#8D9296",
  success: "#2E6B4F",
  white: "#FFFFFF",
  headerBg: "#2E4053",
  sectionBg: "#FFFFFF",
};

const FONT = "'IBM Plex Sans', 'Segoe UI', sans-serif";

function ERPSectionHeader({ title, step }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ pb: 2, mb: 2.5, borderBottom: `1px solid ${COLORS.border}` }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          bgcolor: COLORS.accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>
          {step}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: COLORS.text,
        }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

function ERPTextField({
  label,
  icon: Icon,
  required,
  value,
  onChange,
  ...rest
}) {
  return (
    <TextField
      fullWidth
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      size="small"
      InputProps={{
        startAdornment: Icon ? (
          <InputAdornment position="start">
            <Icon sx={{ fontSize: 16, color: COLORS.textMuted }} />
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        "& .MuiInputBase-root": {
          fontFamily: FONT,
          fontSize: 13,
          bgcolor: COLORS.white,
          borderRadius: 1,
        },
        "& .MuiInputLabel-root": {
          fontFamily: FONT,
          fontSize: 13,
          color: COLORS.textMuted,
        },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: COLORS.accent,
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          { borderColor: COLORS.accent, borderWidth: 1.5 },
        ...rest.sx,
      }}
      {...rest}
    />
  );
}

const CheckoutPage = () => {
  const { cartItems, total, currencySymbol, clearCart } = useCart();
  const navigate = useNavigate();
  const BASE_URL = configLocal.baseURL;
  // ✅ No token → redirect straight to login. { replace: true } means
  //    the checkout page won't be in browser history, so Back doesn't loop.
  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/shop/customerlogin", { replace: true });
    }
  }, []);

  const [address, setAddress] = useState({
    fullName: "",
    phone: "",
    city: "",
    street: "",
    building: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const isFormValid = useMemo(
    () =>
      address.fullName.trim() && address.phone.trim() && address.street.trim(),
    [address]
  );

  const handlePlaceOrder = async () => {
    if (!isFormValid) return;

    const payload = {
      fullName: address.fullName,
      phone: address.phone,
      email: address.email ?? "",
      city: address.city,
      street: address.street,
      building: address.building,
      notes: address.notes,
      items: cartItems.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
        unit_price: item.price,
      })),
      subtotal,
      tax_amount: tax,
      total_amount: grandTotal,
    };
    try {
      const res = await fetch(`${BASE_URL}/api/cart/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        alert(err.error || "Order failed");
        return;
      }

      setSubmitted(true);
      clearCart();
      setTimeout(() => navigate("/shop"), 2200);
    } catch (e) {
      alert("Network error, please try again.");
    }
  };

  const subtotal = total;
  const shipping = 0;
  const tax = +(total * 0.14).toFixed(2);
  const grandTotal = +(subtotal + shipping + tax).toFixed(2);

  if (submitted) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: COLORS.surface,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CheckCircleOutline sx={{ fontSize: 56, color: COLORS.success }} />
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: 20,
              color: COLORS.text,
            }}
          >
            Order Confirmed
          </Typography>
          <Typography
            sx={{ fontFamily: FONT, fontSize: 13, color: COLORS.textMuted }}
          >
            Redirecting to home…
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.surface, fontFamily: FONT }}>
      {/* Header Bar */}
      <Box
        sx={{
          bgcolor: COLORS.headerBg,
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <HomeOutlined
          sx={{ fontSize: 15, color: alpha("#fff", 0.5), cursor: "pointer" }}
          onClick={() => navigate("/shop")}
        />
        <ArrowForwardIos sx={{ fontSize: 9, color: alpha("#fff", 0.3) }} />
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: 12,
            color: alpha("#fff", 0.5),
            cursor: "pointer",
          }}
          onClick={() => navigate("/shop")}
        >
          Shop
        </Typography>
        <ArrowForwardIos sx={{ fontSize: 9, color: alpha("#fff", 0.3) }} />
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: 12,
            color: "#fff",
            fontWeight: 600,
          }}
        >
          Checkout
        </Typography>
      </Box>

      {/* Page Title */}
      <Box
        sx={{ bgcolor: COLORS.headerBg, px: { xs: 2, md: 5 }, pt: 2.5, pb: 3 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography
              sx={{
                fontFamily: FONT,
                color: alpha("#fff", 0.5),
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              Order Processing
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                color: "#fff",
                fontWeight: 700,
                fontSize: 22,
              }}
            >
              New Order — Checkout
            </Typography>
          </Box>
          <Chip
            label={`${cartItems.length} Item${
              cartItems.length !== 1 ? "s" : ""
            }`}
            size="small"
            sx={{
              bgcolor: alpha(COLORS.accent, 0.25),
              color: COLORS.accentLight,
              fontFamily: FONT,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.05em",
              border: "none",
            }}
          />
        </Stack>
      </Box>

      {/* Content */}
      <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 4 } }}>
        <Grid container spacing={2.5}>
          {/* LEFT */}
          <Grid item xs={12} md={8}>
            <Stack spacing={2.5}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 1.5,
                  border: `1px solid ${COLORS.border}`,
                  bgcolor: COLORS.sectionBg,
                }}
              >
                <ERPSectionHeader title="Delivery Information" step="01" />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ERPTextField
                      label="Street Address / Location"
                      icon={LocationOn}
                      required
                      value={address.street}
                      onChange={(e) =>
                        setAddress({ ...address, street: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ERPTextField
                      label="Full Name"
                      icon={PersonOutline}
                      required
                      value={address.fullName}
                      onChange={(e) =>
                        setAddress({ ...address, fullName: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ERPTextField
                      label="Phone Number"
                      icon={PhoneOutlined}
                      required
                      value={address.phone}
                      onChange={(e) =>
                        setAddress({ ...address, phone: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ERPTextField
                      label="Building / Apt #"
                      icon={ApartmentOutlined}
                      value={address.building}
                      onChange={(e) =>
                        setAddress({ ...address, building: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ERPTextField
                      label="City"
                      icon={LocationCityOutlined}
                      value={address.city}
                      onChange={(e) =>
                        setAddress({ ...address, city: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ERPTextField
                      label="Delivery Notes (optional)"
                      value={address.notes}
                      multiline
                      rows={2}
                      onChange={(e) =>
                        setAddress({ ...address, notes: e.target.value })
                      }
                      sx={{
                        "& .MuiInputBase-root": {
                          fontFamily: FONT,
                          fontSize: 13,
                          bgcolor: COLORS.white,
                          borderRadius: 1,
                        },
                        "& .MuiInputLabel-root": {
                          fontFamily: FONT,
                          fontSize: 13,
                          color: COLORS.textMuted,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: COLORS.border,
                        },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                          { borderColor: COLORS.accent },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          { borderColor: COLORS.accent, borderWidth: 1.5 },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 1.5,
                  border: `1px solid ${COLORS.border}`,
                  bgcolor: COLORS.sectionBg,
                }}
              >
                <ERPSectionHeader title="Payment Method" step="02" />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    border: `1.5px solid ${COLORS.accent}`,
                    borderRadius: 1,
                    bgcolor: COLORS.accentLight,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        checked
                        size="small"
                        sx={{
                          color: COLORS.accent,
                          "&.Mui-checked": { color: COLORS.accent },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: 13,
                          color: COLORS.text,
                        }}
                      >
                        Cash on Delivery (COD)
                      </Typography>
                    }
                  />
                  <Chip
                    label="AVAILABLE"
                    size="small"
                    sx={{
                      bgcolor: alpha(COLORS.success, 0.1),
                      color: COLORS.success,
                      fontFamily: FONT,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      height: 20,
                    }}
                  />
                </Box>
              </Paper>
            </Stack>
          </Grid>

          {/* RIGHT */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1.5,
                border: `1px solid ${COLORS.border}`,
                overflow: "hidden",
                position: "sticky",
                top: 16,
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  bgcolor: COLORS.primary,
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ReceiptLongOutlined
                    sx={{ fontSize: 16, color: alpha("#fff", 0.6) }}
                  />
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#fff",
                    }}
                  >
                    Order Summary
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ bgcolor: COLORS.sectionBg }}>
                <Box
                  sx={{
                    px: 3,
                    py: 1,
                    bgcolor: COLORS.surface,
                    borderBottom: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Grid container>
                    {["Item", "Qty", "Amount"].map((h, i) => (
                      <Grid
                        item
                        xs={i === 0 ? 7 : i === 1 ? 2 : 3}
                        key={h}
                        sx={{
                          textAlign:
                            i === 0 ? "left" : i === 1 ? "center" : "right",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            color: COLORS.textMuted,
                            textTransform: "uppercase",
                          }}
                        >
                          {h}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Stack
                  divider={<Divider sx={{ borderColor: COLORS.surface }} />}
                  sx={{ maxHeight: 220, overflowY: "auto" }}
                >
                  {cartItems.map((item) => (
                    <Box key={item.id} sx={{ px: 3, py: 1.5 }}>
                      <Grid container alignItems="center">
                        <Grid item xs={7}>
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 12,
                              fontWeight: 500,
                              color: COLORS.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 12,
                              color: COLORS.textMuted,
                            }}
                          >
                            ×{item.quantity}
                          </Typography>
                        </Grid>
                        <Grid item xs={3} sx={{ textAlign: "right" }}>
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 12,
                              fontWeight: 600,
                              color: COLORS.text,
                            }}
                          >
                            {currencySymbol}
                            {(item.price * item.quantity).toLocaleString()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>

                <Box
                  sx={{
                    borderTop: `1px solid ${COLORS.border}`,
                    px: 3,
                    pt: 2,
                    pb: 1,
                  }}
                >
                  {[
                    {
                      label: "Subtotal",
                      value: `${currencySymbol}${subtotal.toFixed(2)}`,
                    },
                    { label: "Shipping", value: "will be applied" },
                    {
                      label: "VAT (14%)",
                      value: `${currencySymbol}${tax.toFixed(2)}`,
                    },
                  ].map(({ label, value }) => (
                    <Stack
                      key={label}
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 12,
                          color: COLORS.textMuted,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 12,
                          color: COLORS.text,
                        }}
                      >
                        {value}
                      </Typography>
                    </Stack>
                  ))}
                </Box>

                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: `2px solid ${COLORS.primary}`,
                    bgcolor: COLORS.surface,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: 13,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: COLORS.text,
                      }}
                    >
                      Grand Total
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 800,
                        fontSize: 18,
                        color: COLORS.primary,
                      }}
                    >
                      {currencySymbol}
                      {grandTotal.toLocaleString()}
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ px: 3, pb: 3, pt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handlePlaceOrder}
                    disabled={!isFormValid}
                    sx={{
                      bgcolor: COLORS.accent,
                      fontFamily: FONT,
                      fontWeight: 700,
                      fontSize: 13,
                      py: 1.4,
                      borderRadius: 1,
                      textTransform: "none",
                      letterSpacing: "0.04em",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: COLORS.accentHover,
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        bgcolor: alpha(COLORS.accent, 0.3),
                        color: "#fff",
                      },
                    }}
                  >
                    Confirm & Place Order
                  </Button>
                  {!isFormValid && (
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: 11,
                        color: COLORS.textMuted,
                        textAlign: "center",
                        mt: 1,
                      }}
                    >
                      Fill in required fields to continue
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CheckoutPage;
